import React from "react";
import { Link } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowUpRight,
  FiCheck,
} from "react-icons/fi";
import Footer from "../pages/Footer/Footer";
import LegalTableOfContents from "../components/LegalTableOfContents";

const navigation = [
  { id: "overview", label: "Privacy Policy" },
  { id: "cookies", label: "Cookies" },
  { id: "license", label: "License and permitted use" },
  { id: "comments", label: "User comments" },
  { id: "content-liability", label: "Content liability" },
  { id: "reservation", label: "Reservation of rights" },
  { id: "link-removal", label: "Removal of links" },
  { id: "disclaimer", label: "Disclaimer" },
];

const PolicySection = ({ id, number, title, children }) => (
  <section
    id={id}
    className="scroll-mt-28 border-b border-slate-200 py-10 last:border-b-0 md:py-12"
  >
    <div className="mb-5 flex items-center gap-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FFDB16] text-xs font-extrabold text-slate-950">
        {number}
      </span>
      <h2 className="text-2xl font-bold leading-tight text-slate-950 md:text-[30px]">
        {title}
      </h2>
    </div>
    <div className="space-y-4 text-[16px] leading-7 text-slate-600 md:text-[17px]">
      {children}
    </div>
  </section>
);

const PolicyList = ({ children }) => (
  <ul className="space-y-3 rounded-2xl bg-slate-50 p-5">
    {React.Children.map(children, (child) => (
      <li className="flex items-start gap-3">
        <FiCheck className="mt-1 h-5 w-5 shrink-0 text-emerald-600" />
        <span>{child}</span>
      </li>
    ))}
  </ul>
);

const Privacy = () => {
  return (
    <>
      <main className="relative bg-slate-100 px-4 pb-12 pt-32 text-slate-900 md:px-[5%] md:pt-40 lg:pb-8 lg:pt-28">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-32 bg-black md:h-40 lg:h-28"
        />
        <div className="relative z-[1] mx-auto w-full overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 lg:overflow-visible">
          <div className="grid lg:items-start lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="relative overflow-hidden border-b border-white/10 bg-[#101827] text-white lg:sticky lg:top-28 lg:self-start lg:rounded-l-[30px] lg:border-b-0 lg:border-r lg:border-slate-800">
              <div className="absolute -left-20 -top-20 h-52 w-52 rounded-full bg-[#239be6]/25 blur-3xl" />
              <div className="absolute -bottom-20 -right-20 h-52 w-52 rounded-full bg-[#643c90]/35 blur-3xl" />

              <div className="relative flex flex-col p-6 md:p-8 lg:p-7 xl:p-8">
                <LegalTableOfContents
                  items={navigation}
                  ariaLabel="Privacy Policy table of contents"
                  className="mt-0"
                />

              </div>
            </aside>

            <article className="min-w-0 bg-white lg:overflow-hidden lg:rounded-r-[30px]">
              <header className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-white via-sky-50/70 to-purple-50 px-6 py-9 md:px-12 md:py-11 lg:px-16 xl:px-20">
                <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full border-[28px] border-[#239be6]/10" />
                <div className="relative max-w-4xl">
                  <h2 className="font-heading text-3xl font-extrabold leading-tight text-slate-950 md:text-5xl">
                    Privacy Policy
                  </h2>
                </div>
              </header>

              <div className="mx-auto max-w-6xl px-6 md:px-12 lg:px-16 xl:px-20">
            <PolicySection id="overview" number="01" title="Privacy Policy">
              <p>
                Welcome to Abscod Informatics. These terms outline the rules and
                regulations for the use of the Abscod Informatics website,
                located at https://abscod.com/.
              </p>
              <p>
                By accessing this website, we assume you accept these terms and
                conditions. Do not continue to use Abscod Informatics if you do
                not agree to all of the terms stated on this page.
              </p>
              <p>
                The terminology used in this notice, our Terms and Conditions,
                Privacy Statement, Disclaimer Notice, and related agreements is
                interpreted as follows: “Client”, “You”, and “Your” refer to the
                person using this website. “The Company”, “Ourselves”, “We”,
                “Our”, and “Us” refer to Abscod Informatics. “Party” or
                “Parties” refers to both the Client and the Company. Singular,
                plural, and gendered terms are treated as interchangeable where
                the context permits.
              </p>
            </PolicySection>

            <PolicySection id="cookies" number="02" title="Cookies">
              <p>
                We use cookies. By accessing Abscod Informatics, you agree to
                the use of cookies in accordance with this policy.
              </p>
              <p>
                Most interactive websites use cookies to retrieve user details
                for each visit. Our website uses cookies to enable certain
                functionality and make the experience easier for visitors. Some
                affiliate or advertising partners may also use cookies.
              </p>
            </PolicySection>

            <PolicySection id="license" number="03" title="License and permitted use">
              <p>
                Unless otherwise stated, Abscod Informatics and/or its licensors
                own the intellectual property rights for all material on this
                website. All intellectual property rights are reserved. You may
                access the material for your own personal use, subject to the
                restrictions in this notice.
              </p>
              <p className="font-semibold text-slate-900">You must not:</p>
              <PolicyList>
                <span>Republish material from Abscod Informatics.</span>
                <span>Sell, rent, or sublicense material from Abscod Informatics.</span>
                <span>Reproduce, duplicate, or copy material from Abscod Informatics.</span>
                <span>Redistribute content from Abscod Informatics.</span>
              </PolicyList>
            </PolicySection>

            <PolicySection id="comments" number="04" title="User comments">
              <p>
                Parts of this website may allow users to post and exchange
                opinions and information. Abscod Informatics does not
                necessarily filter, edit, publish, or review comments before
                they appear. Comments reflect the views of the person who posts
                them and do not necessarily reflect the views of Abscod
                Informatics, its agents, or affiliates.
              </p>
              <p>
                To the extent permitted by applicable law, Abscod Informatics
                is not liable for comments or for liability, damages, or
                expenses caused by their use, posting, or appearance on this
                website. We reserve the right to monitor and remove comments
                that are inappropriate, offensive, or breach these terms.
              </p>
              <p className="font-semibold text-slate-900">
                By posting a comment, you warrant that:
              </p>
              <PolicyList>
                <span>You are entitled to post it and have the necessary licenses and consent.</span>
                <span>It does not infringe any third-party copyright, patent, trademark, or other intellectual property right.</span>
                <span>It does not contain defamatory, offensive, indecent, unlawful, or privacy-invasive material.</span>
                <span>It will not be used to solicit business or promote unlawful activity.</span>
              </PolicyList>
              <p>
                You grant Abscod Informatics a non-exclusive license to use,
                reproduce, and edit your comments, and to authorize others to do
                so, in any form, format, or media.
              </p>
            </PolicySection>

            <PolicySection id="content-liability" number="05" title="Content liability">
              <p>
                We are not responsible for content that appears on your website.
                You agree to protect and defend us against claims arising from
                your website. No link should appear on a website if it may be
                interpreted as libelous, obscene, or criminal, or if it
                infringes, violates, or advocates the infringement of any
                third-party right.
              </p>
            </PolicySection>

            <PolicySection id="reservation" number="06" title="Reservation of rights">
              <p>
                We reserve the right to request the removal of all links, or a
                particular link, to our website. You agree to remove links to
                our website promptly upon request. We also reserve the right to
                amend these terms and our linking policy at any time. By
                continuing to link to our website, you agree to follow these
                linking terms.
              </p>
            </PolicySection>

            <PolicySection id="link-removal" number="07" title="Removal of links from our website">
              <p>
                If you find a link on our website that is offensive for any
                reason, you may contact us at any time. We will consider requests
                to remove links, but we are not obligated to remove them or to
                respond directly.
              </p>
              <p>
                We do not guarantee that all information on this website is
                correct, complete, or current. We also do not promise that the
                website will always remain available or that its material will
                always be kept up to date.
              </p>
            </PolicySection>

            <PolicySection id="disclaimer" number="08" title="Disclaimer">
              <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-950">
                <FiAlertCircle className="mt-0.5 h-6 w-6 shrink-0" />
                <p>
                  To the maximum extent permitted by applicable law, we exclude
                  representations, warranties, and conditions relating to this
                  website and its use.
                </p>
              </div>
              <p>Nothing in this disclaimer will:</p>
              <PolicyList>
                <span>Limit or exclude liability for death or personal injury.</span>
                <span>Limit or exclude liability for fraud or fraudulent misrepresentation.</span>
                <span>Limit liabilities in a way that is not permitted under applicable law.</span>
                <span>Exclude liabilities that may not be excluded under applicable law.</span>
              </PolicyList>
              <p>
                These limitations apply to liabilities arising in contract, in
                tort, and for breach of statutory duty. As long as the website
                and its information and services are provided free of charge,
                we will not be liable for loss or damage of any nature to the
                extent permitted by applicable law.
              </p>
            </PolicySection>

            <section className="my-10 overflow-hidden rounded-3xl bg-[#101827] p-6 text-white md:p-7">
              <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[auto_minmax(0,1fr)_auto] xl:items-center xl:gap-4">
                <h2 className="shrink-0 whitespace-nowrap font-heading text-xl font-bold text-white 2xl:text-2xl">
                  We’re here to help
                </h2>
                <p
                  className="min-w-0 text-sm leading-6 text-purple-100 xl:overflow-hidden xl:text-ellipsis xl:whitespace-nowrap"
                  title="Contact our team if you have a question about this notice or how it applies to your use of the website."
                >
                  Contact our team if you have a question about this notice or
                  how it applies to your use of the website.
                </p>
                <Link
                  to="/contactus"
                  className="type-button inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-[#FFDB16] px-5 py-2.5 text-slate-950 transition hover:bg-white xl:justify-self-end"
                >
                  Contact Us <FiArrowUpRight />
                </Link>
              </div>
            </section>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Privacy;
